/**
 * 条码打印工具 — 从 BarcodePrint.vue 的 doPrint 核心逻辑提取
 * 用于在其他页面（如退货）直接调用打印，不需要路由和完整页面
 */
import { submit } from '@/api/base'
import JsBarcode from 'jsbarcode'
import QRCode from 'qrcode'
import { initPrint, getPrinters } from '@/utils/lodopWrapper'

/**
 * 获取条码打印配置（BarCodePrintSetting）
 * @returns {Promise<Object>} 打印配置对象
 */
export async function getBarcodePrintConfig() {
  try {
    const res = await submit('/api/common/getCustomerParameter?key=BarCodePrintSetting')
    if (res.success && res.data) {
      const config = JSON.parse(res.data)
      return {
        paperWidth: config.paperWidth || 40,
        paperHeight: config.paperHeight || 30,
        codeType: config.codeType || 'barcode',
        printLayout: config.printLayout || 'single',
        textPosition: config.textPosition || 'bottom',
        scanContent: config.scanContent || 'barcode',
        showSpec: config.showSpec !== false,
        fontSize: config.fontSize || 12,
      }
    }
  } catch (error) {
    console.error('获取条码打印配置失败', error)
  }
  // 默认配置
  return {
    paperWidth: 40,
    paperHeight: 30,
    codeType: 'barcode',
    printLayout: 'single',
    textPosition: 'bottom',
    scanContent: 'barcode',
    showSpec: true,
    fontSize: 12,
  }
}

/**
 * 执行条码打印
 * @param {Array} specList - 规格列表，每项需包含：Barcode/Sku, ItemNo, Color, Size, count
 * @param {Object} config - 打印配置（由 getBarcodePrintConfig 返回）
 * @param {string} printerName - 打印机名称，传空则弹窗选择
 * @returns {Promise<void>}
 */
export async function doBarcodePrint(specList, config, printerName) {
  if (!specList || specList.length === 0) {
    throw new Error('没有可打印的规格')
  }

  // 构建打印列表（按 count 展开）
  const printList = []
  specList.forEach(spec => {
    const count = spec.count || 1
    for (let i = 0; i < count; i++) {
      printList.push(spec)
    }
  })

  const w = config.paperWidth || 40
  const h = config.paperHeight || 30
  const isQRCode = config.codeType === 'qrcode'
  const isDouble = config.printLayout === 'double'
  let textPos = config.textPosition
  // 一维码不支持左/右文字位置，默认改成码下方
  if (!isQRCode && textPos !== 'top' && textPos !== 'bottom') {
    textPos = 'bottom'
  }
  // 选择打印机
  let curPrinter = printerName || ''
  if (!curPrinter) {
    const printers = getPrinters()
    if (printers.length === 0) {
      throw new Error('未检测到打印机，请检查打印组件是否安装')
    }
    curPrinter = printers[0].label
  }

  // 内容区域可用宽度：去掉左右各 2mm
  const contentW = w - 4


  // 计算条码尺寸
  let codeW, codeH
  // 因为像素点和mm的比例不一致，所以需要根据比例计算出对应的宽度和高度
  const pxRatio = 1.5;
  if (isQRCode) {
    // 二维码：码上/下方时 = 纸张高度的40%，码左/右方时 = 纸张高度的60%，宽高一致
    const ratio = (textPos === 'left' || textPos === 'right') ? 0.5 : 0.4
    codeW = Math.round(h * ratio * pxRatio)
    codeH = codeW
  } else {
    // 一维码：高度 = 纸张高度的50%，宽度使用内容区域全宽（去掉左右各1mm边距）
    codeW = w - 2
    codeH = Math.round(h * 0.5)
  }

  // 文字区域估算高度：每行约 fontSize * 1.4 / 25.4 mm，行间距 2mm
  const lineHeightMm = (config.fontSize || 12) * 1.4 / 25.4 * 10 // pt -> mm 近似
  const lineCount = config.showSpec ? 2 : 1
  const textTotalH = lineCount * lineHeightMm + (lineCount - 1) * 2 // 行间距2mm
  const gap = 1 // 文字与条码间距 1mm

  /**
   * 计算单个条码在纸张区域内的布局坐标（相对于区域左上角）
   * 返回 { codeLeft, codeTop, textLeft, textTop, textWidth, textHeight, textAlign }
   */
  const calcLayout = () => {
    let codeLeft, codeTop, textLeft, textTop, textWidth, textHeight
    let align = 1 // lodop Alignment: 1=左对齐, 2=居中, 3=右对齐

    if (isQRCode) {
      if (textPos === 'top') {
        // 码上方：文字顶部间距2mm，条码顶部与文字最后一行间距2mm，左对齐条码，条码水平居中
        codeLeft = Math.round((w - codeW) / 2) + 'mm'
        textLeft = codeLeft
        textWidth = codeW + 'mm'
        textHeight = textTotalH + 'mm'
        textTop = '2mm'
        codeTop = Math.round(2 + textTotalH + gap) + 'mm'
      } else if (textPos === 'left') {
        // 码左方：条码垂直居中/水平居右，文字在条码左方，文字右对齐
        align = 3
        codeLeft = Math.round(w - 2 - codeW) + 'mm'
        textLeft = '2mm'
        textWidth = Math.round(contentW - codeW - gap) + 'mm'
        textHeight = codeH + 'mm'
        const textActualH = lineCount * lineHeightMm
        const textOffset = (codeH - textActualH) / 2
        textTop = Math.round((h - codeH) / 2 + textOffset) + 'mm'
        codeTop = Math.round((h - codeH) / 2) + 'mm'
      } else if (textPos === 'right') {
        // 码右方：条码垂直居中/水平居左，文字在条码右方，文字左对齐
        codeLeft = '2mm'
        textLeft = Math.round(codeW + gap) + 'mm'
        textWidth = Math.round(contentW - codeW - gap) + 'mm'
        textHeight = codeH + 'mm'
        const textActualH = lineCount * lineHeightMm
        const textOffset = (codeH - textActualH) / 2
        textTop = Math.round((h - codeH) / 2 + textOffset) + 'mm'
        codeTop = Math.round((h - codeH) / 2) + 'mm'
      } else {
        // 码下方：条码顶部间距2mm，文字第一行与条码底部间距2mm，左对齐条码，条码水平居中
        codeLeft = Math.round((w - codeW) / 2) + 'mm'
        codeTop = '2mm'
        textLeft = codeLeft
        textWidth = codeW + 'mm'
        textHeight = textTotalH + 'mm'
        textTop = Math.round(codeH) + 'mm'
      }
    } else {
      // 一维码：条码距离左侧2mm，商品信息左对齐条码
      codeLeft = 2 + 'mm'
      textLeft = codeLeft
      textWidth = codeW + 'mm'
      textHeight = textTotalH + 'mm'
      if (textPos === 'top') {
        textTop = '2mm'
        codeTop = Math.round(gap + textTotalH + gap) + 'mm'
      } else {
        // bottom
        codeTop = '2mm'
        textTop = Math.round(2 + codeH + gap) + 'mm'
      }
    }
    return { codeLeft, codeTop, textLeft, textTop, textWidth, textHeight, align }
  }

  const layout = calcLayout()

  // 计算总页数（供进度和回调使用）
  const totalPages = isDouble ? Math.ceil(printList.length / 2) : printList.length

  // LODOP On_Return 回调：打印窗口关闭时触发
  // Action: 1=打印成功, 2=打印取消/关闭窗口, 其他值忽略（如初始化时可能触发0）
  let printReturned = false // 防重复触发
  let fallbackTimer = null  // 兜底定时器

  const hideProgress = () => {
    clearTimeout(fallbackTimer)
    this.printing = false
    this.printPercent = 0
    this.printStatus = ''
  }

  const onPrintReturn = (action) => {
    if (printReturned) return

    // Action 可能是数字 1 或字符串任务ID（如 "08AAAAM113258_37"），真值即为成功提交
    // Action 为 2 表示取消，空字符串/0 为初始化状态，忽略
    if (action === 2) {
      printReturned = true
      clearTimeout(fallbackTimer)
      this.printPercent = 0
      this.printStatus = 'exception'
      this.printStatusText = '打印已取消'
    } else if (action) {
      // 打印已提交/action 为真值：1 或非空任务ID）
      printReturned = true
      clearTimeout(fallbackTimer)
      this.printPercent = 100
      this.printStatus = 'success'
      this.printStatusText = `已推送打印，共 ${totalPages} 页`
    }
    setTimeout(hideProgress, 2000)
  }

  // 页面高度多加 10mm 空白，用于撕纸时保护内容不被截断
  const extraBottom = 0
  // 双排时逻辑宽度 = w * 2 + 2mm间距
  const pageW = isDouble ? w * 2 + 2 : w
  const chain = initPrint('商品条码打印', this.curPrinter, onPrintReturn, pageW + 'mm', (h + extraBottom) + 'mm', false)

  /**
   * 在指定水平偏移处打印一个规格
   * @param {number} offsetX - 水平偏移(mm)，单排为0，双排第二列为 w+2
   */
  const printSpec = (spec, offsetX) => {
    const barcode = spec.Barcode || spec.Sku || ''
    const itemNo = spec.ItemNo || ''

    // 只对水平坐标加偏移
    const offsetH = (valStr) => {
      if (offsetX === 0) return valStr
      const val = parseFloat(valStr)
      return Math.round(val + offsetX) + 'mm'
    }

    // 先重置字体样式（避免上一轮 setStyle 影响条码自动文字大小）
    chain.setStyle('FontSize', 8)
    chain.setStyle('Bold', 0)

    // 条码/二维码
    console.log("codeW:" + codeW + "codeH:" + codeH)
    if (isQRCode) {
      const qrContent = config.scanContent === 'barcode'
        ? barcode
        : `${itemNo} ${spec.Color || ''} ${spec.Size || ''}`
      chain.addBarcode(layout.codeTop, offsetH(layout.codeLeft), codeW + 'mm', codeH + 'mm', qrContent, 'QRCode')
    } else {
      chain.addBarcode(layout.codeTop, offsetH(layout.codeLeft), codeW + 'mm', codeH + 'mm', barcode, '128Auto')
    }

    // 文字：单独设置商品信息的字体样式
    let textContent = `${itemNo}`
    if (config.showSpec) {
      if (isQRCode) {
        // 二维码：颜色、尺码分两行显示
        textContent += `\n${spec.Color || ''}\n${spec.Size || ''}`
      } else {
        // 一维码：颜色、尺码在同一行
        textContent += `\n${spec.Color || ''} ${spec.Size || ''}`
      }
    }
    chain.setStyle('FontSize', config.fontSize || 12)
    chain.setStyle('Bold', 1)
    if (isQRCode) {
      chain.setStyle('Alignment', layout.align)
    }
    chain.addText(layout.textTop, offsetH(layout.textLeft), layout.textWidth, layout.textHeight, textContent)
  }

  // 关闭对话框，显示打印进度
  this.selectPrint = false
  this.printing = true
  this.printPercent = 0
  this.printStatus = ''
  this.printStatusText = `正在准备打印，共 ${totalPages} 页...`

  // 延迟执行打印，等对话框关闭动画完成
  await new Promise(resolve => setTimeout(resolve, 300))

  this.printStatusText = '正在生成打印内容...'

  if (isDouble) {
    // 双排：每页两个规格，中间间距 2mm
    const doubleGap = 2 // 两个区域之间间距 2mm
    for (let i = 0; i < printList.length; i += 2) {
      if (i > 0) chain.newPage()
      // 第一个规格，偏移 0
      printSpec(printList[i], 0)
      // 第二个规格，偏移 w + doubleGap
      if (i + 1 < printList.length) {
        printSpec(printList[i + 1], w + doubleGap)
      }
      this.printPercent = Math.round((i + 2) / printList.length * 90)
      this.printStatusText = `正在生成第 ${Math.floor(i / 2) + 1}/${totalPages} 页...`
    }
  } else {
    // 单排：每页一个规格
    printList.forEach((spec, index) => {
      if (index > 0) chain.newPage()
      printSpec(spec, 0)
      this.printPercent = Math.round((index + 1) / printList.length * 90)
      this.printStatusText = `正在生成第 ${index + 1}/${totalPages} 页...`
    })
  }

  this.printPercent = 95
  this.printStatusText = '正在发送打印任务...'
  // 调用打印，LODOP 打开打印窗口
  // 打印完成/取消后由 initPrint 中注册的 onPrintReturn 回调处理进度状态
  chain.print()

  // 兜底：如果 LODOP On_Return 一直不触发（直接打印无对话框场景），8秒后自动隐藏进度
  fallbackTimer = setTimeout(() => {
    if (!printReturned) {
      printReturned = true
      hideProgress()
    }
  }, 8000)
}

export default { getBarcodePrintConfig, doBarcodePrint }
